import PageContent from '@/components/PageContent';
import { AdvanceShareStylePolicyDefinition, AdvanceShareStylePolicyTypeList } from '@/models/advance-share-style-definition.model';
import { AdvanceShareStyleVersion, AdvanceShareStyleVersionStatusEnum, UpsertAdvanceShareStyleVersion } from '@/models/advance-share-style-version.model';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { upsertAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { AxiosError } from 'axios';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Schema,
  SelectPicker,
  Stack,
  Divider,
  IconButton,
  getHeight,
} from 'rsuite';
import { useNavigate } from 'react-router-dom';

const { Column, HeaderCell, Cell } = Table;

interface AdvanceShareStyleVersionPoliciesProps {
  formObject?: AdvanceShareStyleVersion;
  fetchData: () => void;
}

const AdvanceShareStyleVersionPolicies: React.FC<AdvanceShareStyleVersionPoliciesProps> = ({ formObject, fetchData }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | undefined>(undefined);
  const [editingPolicy, setEditingPolicy] = useState<AdvanceShareStylePolicyDefinition | undefined>(undefined);
  const [value, setValue] = useState<AdvanceShareStylePolicyDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const nav = useNavigate();


  const model = Schema.Model({
    type: Schema.Types.NumberType().isRequired('Type is required'),
  });

  useEffect(() => {
    if(formObject){
      setValue(formObject.policies ?? []);
      setDisabled(!!formObject && formObject.status != AdvanceShareStyleVersionStatusEnum.Draft);
    } else {
      setValue([]);
      setDisabled(false);
    }
  }, [formObject]);
  
  const handleUpdateStyleVersion = async (updatedValue: AdvanceShareStylePolicyDefinition[]) => {
    setLoading(true);
    try {
      const castedModel: UpsertAdvanceShareStyleVersion = { 
        ...formObject, policies: updatedValue.length == 0 ? undefined : updatedValue
      };      
      const result: BaseResponseModel<any> = await upsertAdvanceShareStyleVersion(castedModel);
      if (result.success) {
        toastSuccessMessage("Usage rules successfully updated.");
        fetchData();
      }
    } catch (err) {
      const errorMsg = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage("Error on updating style version policies: " + errorMsg);
    }finally{
      setLoading(false);
    }
  };

  const handleModalSubmit = (formValue: AdvanceShareStylePolicyDefinition) => {
    if(value.some((item, index) => 
      item.type === formValue.type &&
      index !== editingRowIndex)){
        // validate unique key
        toastErrorMessage('Policy type must be unique');
        return;
    }

  let updated: AdvanceShareStylePolicyDefinition[];
  if (editingRowIndex !== undefined) {
    updated = [...value];
    updated[editingRowIndex] = formValue;
  } else {
    updated = [...value, formValue];
  }
    
    handleUpdateStyleVersion(updated);
    setShowModal(false);
    setEditingPolicy(undefined);
  };
    
  const handleDeleteRow = (rowIndex?: number) => {
    const updated = [...value];
    updated.splice(rowIndex!, 1);
    setValue(updated);
    handleUpdateStyleVersion(updated);
  };
  
  const handleFormChange = (value: Record<string, any>) => {
    setEditingPolicy(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };


  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between"> 
        <Button appearance="primary" disabled = {disabled || loading} onClick={() => {
            setShowModal(true);
            setEditingPolicy({ type: undefined});
            setEditingRowIndex(undefined);
        }}>
          Add Rule
        </Button>
      </Stack>

      <Table data={value} height={Math.max(getHeight(window) - 350, 400)}
>
        <Column flexGrow={1}>
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(rowData : AdvanceShareStylePolicyDefinition )=>
              AdvanceShareStylePolicyTypeList.findLast(t => t.value === rowData.type)?.label
            }
          </Cell>
        </Column>
        
        <Column width={120}>
          <HeaderCell>Actions</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareStylePolicyDefinition, rowIndex?: number) => (
              <Stack spacing={6}>
                  <Button
                      color='red'
                      appearance='ghost'
                      size="xs"
                      disabled = {disabled || loading}
                      onClick={() => handleDeleteRow(rowIndex)}
                  >
                  X
                  </Button>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      
      <Divider />

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
      </Stack>

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title>
            {editingRowIndex !== undefined ? 'Edit Policy' : 'Add Policy'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            fluid
            model={model}
            formValue={editingPolicy}
            onChange={handleFormChange}
            onSubmit={formValue => handleModalSubmit(formValue as AdvanceShareStylePolicyDefinition)}
          >
            <Form.Group controlId="type">
              <Form.Label>Type</Form.Label>
              <Form.Control
                name="type"
                accepter={SelectPicker}
                data={AdvanceShareStylePolicyTypeList}
                block
                searchable={false}
              />
            </Form.Group>
            
            <Button appearance="primary" type="submit">
              Save
            </Button>
          </Form>
        </Modal.Body>
      </Modal>        
    </PageContent>
  );
};

export default AdvanceShareStyleVersionPolicies;
